import { Button, THEME } from "@myloc/myloc-gui";
import classNames from "classnames";
import format from "date-fns/format";
import PropType from "prop-types";
import { useCallback, useEffect, useState } from "react";
import { generatePath, Link, useLocation } from "react-router-dom";
import { ChevronDown, ChevronUp } from "../../../assets/icons";
import Problem from "../../../assets/icons/Problem";
import { useTranslate } from "../../../language/i18n";
import clientService from "../../../services/client/clientService";
import pages from "../../../utils/pages";
import { useMobile, useTablet } from "../../../utils/viewport";
import Checkbox from "../Checkbox/Checkbox";
import Counter from "../Counter/Counter";
import CustomizedBadge from "../CustomizedBadge/CustomizedBadge";
import InventoryAccessoryRow from "../InventoryAccessoryRow/InventoryAccessoryRow";
import Label from "../Label/Label";
import { pageSize } from "../Pagination/PageSize";
import Paginate from "../Pagination/Paginate";
import RadioButton from "../RadioButton/RadioButton";
import LoadingSpinner from "../Spinner/LoadingSpinner";
import styles from "./ClientHoldingList.module.scss";

function ClientHoldingList({
  onSelect,
  clientId,
  selected = [],
  singleSelect,
  multiSelect,
  readOnly,
  link,
  amountChangeable,
  onAmountChange,
  provider,
  referrer,
  maxSelectable,
  onlyInventories,
  skipSearch = false,
  disableAllocated,
  isReceiverMenu,
  isHistory,
}) {
  const translate = useTranslate();
  const [data, setData] = useState([]);
  const [holdingList, setHoldingList] = useState([]);
  const [holdingHistoryList, setHoldingHistoryList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  useEffect(() => {
    if (isHistory) {
      setHoldingHistoryList(data);
    } else {
      setHoldingList(data);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);
  const getProvider = useCallback(
    filter => {
      if (provider) {
        return provider(filter);
      } else if (isHistory) {
        return clientService.getHoldingHistory({ clientId, filter: { ...filter, sort: "handedOverDate:desc" } });
      } else {
        return clientService.getHolding({
          clientId,
          filter: { ...filter, sort: "handedOverDate:desc" },
          onlyInventory: onlyInventories,
        });
      }
    },
    [provider, isHistory, clientId, onlyInventories],
  );

  const toggleSelected = row => {
    const exists = selected?.some(item => item?.id === row.id);
    const selection = exists ? selected?.filter(item => item.id !== row.id) : [...selected, row];
    onSelect(selection);
  };

  const getQuantity = row => {
    const item = data?.find(item => item.id === row.id);
    const maxQuantity = item?.maxQuantity >= 0 ? item?.maxQuantity : item?.quantity - item?.allocatedQuantity;
    return maxQuantity;
  };

  const itemsAvailable = data?.filter(item => item.maxQuantity > 0 || item.quantity - item.allocatedQuantity > 0);
  return (
    <>
      {multiSelect && !maxSelectable?.amount && holdingList?.length > 1 && (
        <Checkbox
          onChange={() => (selected.length === itemsAvailable.length ? onSelect([]) : onSelect([...itemsAvailable]))}
          checked={selected?.length === itemsAvailable?.length}
          label={translate("SELECT_ALL")}
          customCssClass={styles.selectAll}
        />
      )}
      <Paginate
        setIsLoading={setIsLoading}
        isLoading={isLoading}
        onChange={setData}
        provider={getProvider}
        skipSearch={skipSearch}
        defaultPageSize={pageSize.LARGE}
      >
        <section className={styles.results}>
          {isLoading ? (
            <LoadingSpinner title="HOLDINGS_LOADING" />
          ) : holdingHistoryList.length ? (
            holdingHistoryList?.map(row => {
              return <HistoryReadOnlyRow row={row} key={row.id} />;
            })
          ) : (
            holdingList?.map(row => {
              if (readOnly) {
                return <ReadOnlyRow row={row} link={link} key={row.id} referrer={referrer} />;
              }
              if (multiSelect) {
                return (
                  <MultiSelectRow
                    row={row}
                    link={link}
                    selected={selected}
                    amountChangeable={amountChangeable}
                    onAmountChange={onAmountChange}
                    toggleSelected={toggleSelected}
                    getQuantity={getQuantity}
                    key={row.id}
                    referrer={referrer}
                    maxSelectableAmount={maxSelectable?.amount}
                    disableAllocated={disableAllocated}
                    isReceiverMenu={isReceiverMenu}
                  />
                );
              }
              if (singleSelect) {
                return (
                  <SingleSelectRow
                    row={row}
                    link={link}
                    selected={selected}
                    amountChangeable={amountChangeable}
                    onAmountChange={onAmountChange}
                    onSelect={onSelect}
                    getQuantity={getQuantity}
                    key={row.id}
                    referrer={referrer}
                  />
                );
              }
            })
          )}
        </section>
      </Paginate>
      {maxSelectable?.amount < selected.length && <p className={styles.error}>{maxSelectable.text}</p>}
    </>
  );
}

const AccessorySection = ({ row }) => {
  const translate = useTranslate();
  const [expanded, setExpanded] = useState(false);
  if (!row.accessories?.length) return null;
  return (
    <section className={styles.accessorySection}>
      <Button theme={THEME.SECONDARY} onClick={() => setExpanded(prev => !prev)}>
        {translate(expanded ? "HIDE_ACCESSORIES" : "SHOW_ACCESSORIES")}
        {expanded ? (
          <ChevronUp size={16} customCssClass={styles.accessoryChevron} />
        ) : (
          <ChevronDown size={16} customCssClass={styles.accessoryChevron} />
        )}
      </Button>
      {expanded && row.accessories.map((acc, idx) => <InventoryAccessoryRow key={acc.id ?? idx} accessory={acc} />)}
    </section>
  );
};

const TitleName = ({ row, link, referrer, isReceiverMenu }) => {
  const location = useLocation();
  if (!link) {
    return (
      <>
        {row.name}
        {row.hasDeviation && <Problem customCssClass={styles.deviation} />}
      </>
    );
  }
  const state = isReceiverMenu !== undefined ? { referrer, isReceiverMenu } : { referrer };
  return (
    <>
      <Link to={{ pathname: `${location.pathname}/${row.id}`, state }}>{row.name}</Link>
      {row.hasDeviation && <Problem customCssClass={styles.deviation} />}
    </>
  );
};

const ReadOnlyRow = ({ row, link, referrer }) => {
  const isMobile = useMobile();

  return (
    <article className={styles.item}>
      {isMobile ? (
        <div className={styles.mobileSection}>
          <p className>
            <TitleName row={row} link={link} referrer={referrer} />
          </p>
          <p>
            {row.quantity} {row.unit}
          </p>
        </div>
      ) : (
        <section className={classNames(styles.titleSection, row.deviation && styles.titleDeviation)}>
          <p className={styles.itemTitle}>
            <TitleName row={row} link={link} referrer={referrer} />
          </p>

          <p>
            {row.quantity} {row.unit}
          </p>
        </section>
      )}

      <InfoSection row={row} link={link} />
      <AccessorySection row={row} />
    </article>
  );
};

const HistoryReadOnlyRow = ({ row }) => {
  const translate = useTranslate();
  const positiveQuantity = row.quantity > 0 ? row.quantity : row.quantity * -1;
  return (
    <article className={classNames(styles.item)}>
      <section className={classNames(styles.titleSection, row.deviation && styles.titleDeviation)}>
        <p className={styles.itemTitle}>{row.itemDescription}</p>
        <p>
          {positiveQuantity} {row.unit ? row.unit : translate("UNITS_FULL")}
        </p>
      </section>
      <HistoryInfoSection row={row} />
    </article>
  );
};

const MultiSelectRow = ({
  selected,
  row,
  amountChangeable,
  onAmountChange,
  toggleSelected,
  getQuantity,
  link,
  referrer,
  maxSelectableAmount,
  disableAllocated,
  isReceiverMenu,
}) => {
  const isSelected = selected?.some(item => item?.id === row.id);
  const error = isSelected && maxSelectableAmount < selected.length;
  const maxQuantity = row.maxQuantity >= 0 ? row.maxQuantity : row.quantity - row.allocatedQuantity;
  const isMobile = useMobile();

  const isDisabled = () => {
    if (row.handedOverInformation != null) return true;
    if (disableAllocated) return maxQuantity === 0 ?? false;
    return false;
  };

  const disabled = isDisabled();

  return (
    <article className={classNames(styles.item, error && styles.error, disabled && styles.disabled)} key={row.id}>
      <Checkbox onChange={() => toggleSelected(row)} checked={isSelected} disabled={disabled} />
      {isMobile ? (
        <div className={styles.mobileSection}>
          <p className>
            <TitleName row={row} link={link} referrer={referrer} />
          </p>
          <p>
            {row.quantity} {row.unit}
          </p>
        </div>
      ) : (
        <section className={classNames(styles.titleSection, row.deviation && styles.titleDeviation)}>
          <p className={styles.itemTitle}>
            <TitleName row={row} link={link} referrer={referrer} isReceiverMenu={isReceiverMenu} />
          </p>

          {amountChangeable ? (
            <>
              {isSelected ? (
                <Counter
                  onChange={num => onAmountChange(row, num)}
                  min={1}
                  max={maxQuantity}
                  startValue={getQuantity(row)}
                  customCssClass={classNames(styles.counter, isSelected && styles.selected)}
                />
              ) : (
                <p>
                  {row.quantity} {row.unit}
                </p>
              )}
            </>
          ) : (
            <p>
              {row.quantity} {row.unit}
            </p>
          )}
        </section>
      )}

      <InfoSection row={row} link={link} />
      <AccessorySection row={row} />
    </article>
  );
};

const SingleSelectRow = ({
  selected,
  row,
  onSelect,
  amountChangeable,
  onAmountChange,
  getQuantity,
  link,
  referrer,
}) => {
  const isSelected = selected.some(item => item?.id === row.id);
  const isMobile = useMobile();

  return (
    <article className={styles.item} key={row.id}>
      <RadioButton
        id={row.id}
        name="aid"
        onChange={() => {
          !isSelected ? onSelect(row) : onSelect();
        }}
        customCssClass={styles.check}
        checked={isSelected}
      />
      {isMobile ? (
        <div className={styles.mobileSection}>
          <p className>
            <TitleName row={row} link={link} referrer={referrer} />
          </p>
          <p>
            {row.quantity} {row.unit}
          </p>
        </div>
      ) : (
        <section className={classNames(styles.titleSection, row.deviation && styles.titleDeviation)}>
          <p className={styles.itemTitle}>
            <TitleName row={row} link={link} referrer={referrer} />
          </p>

          {amountChangeable ? (
            <>
              {isSelected ? (
                <Counter
                  onChange={num => onAmountChange(row, num)}
                  min={1}
                  max={row.quantity}
                  startValue={getQuantity(row)}
                  customCssClass={classNames(styles.counter, isSelected && styles.selected)}
                />
              ) : (
                <p>
                  {row.quantity} {row.unit}
                </p>
              )}
            </>
          ) : (
            <p>
              {row.quantity} {row.unit}
            </p>
          )}
        </section>
      )}

      <InfoSection row={row} link={link} />
      <AccessorySection row={row} />
    </article>
  );
};

const ProductNumberLabel = ({ row, link }) => {
  const translate = useTranslate();
  const label = <Label inline label={translate("PRODUCT_NUMBER_SHORT")} value={row.productNumber} />;
  if (!link) return label;
  return (
    <Link
      to={{
        pathname: generatePath(pages.PRODUCT_DETAIL.PATH, { product: row.productId }),
        state: { referrer: `${window.location.pathname}${window.location.search}` },
      }}
    >
      {label}
    </Link>
  );
};

const InfoSection = ({ row, link }) => {
  const translate = useTranslate();
  const isTablet = useTablet();
  return (
    <section className={styles.infoSection}>
      {isTablet ? (
        <>
          <div>
            <ProductNumberLabel row={row} link={link} />
            <Label inline label={translate("ARTICLE_CLASS")} value={row?.itemClass?.label} />
          </div>
          <div className={styles.right}>
            {row?.allocatedQuantity > 0 && (
              <Label inline label={translate("HOLDING_RESERVED")} value={`${row.allocatedQuantity} ${row.unit}`} />
            )}
            {row.handedOverInformation && (
              <p className={(styles.text, styles.highlightedYellow)}> {row.handedOverInformation} </p>
            )}
            {row.handedOverDate && <Label inline label={translate("DELIVERED_DATE")} value={row.handedOverDate} />}
            <Label inline label={translate("PRODUCT_OWNER")} value={row.goodsOwner?.label} />
            <Label inline label={translate("STORAGE_PLACE")} value={row.warehouseLocation.label} />
          </div>
          <div>
            <div className={styles.inventoryLine}>
              <Label inline label={translate("INVENTORY_NUMBER_SHORT")} value={row.inventoryNumber} />
              {row.customized && <CustomizedBadge />}
            </div>
            <Label inline label={translate("BATCH")} value={row.batch} />
          </div>
          {row.status && (
            <div>
              <div>{row.status}</div>
            </div>
          )}
        </>
      ) : (
        <>
          <div>
            <ProductNumberLabel row={row} link={link} />
            <Label inline label={translate("ARTICLE_CLASS")} value={row?.itemClass?.label} />
          </div>
          <div>
            <div className={styles.inventoryLine}>
              <Label inline label={translate("INVENTORY_NUMBER_SHORT")} value={row.inventoryNumber} />
              {row.customized && <CustomizedBadge />}
            </div>
            <Label inline label={translate("BATCH_NO")} value={row.batch} />
          </div>
          <div>
            <div>{row.status}</div>
          </div>
          <div className={styles.right}>
            {row?.allocatedQuantity > 0 && (
              <Label inline label={translate("HOLDING_RESERVED")} value={`${row.allocatedQuantity} ${row.unit}`} />
            )}
            {row.handedOverInformation && (
              <p className={(styles.text, styles.highlightedYellow)}> {row.handedOverInformation} </p>
            )}
            {row.handedOverDate && <Label inline label={translate("DELIVERED_DATE")} value={row.handedOverDate} />}
            <Label inline label={translate("PRODUCT_OWNER")} value={row.goodsOwner?.label} />
            <Label inline label={translate("STORAGE_PLACE")} value={row.warehouseLocation.label} />
          </div>
        </>
      )}
    </section>
  );
};

const HistoryInfoSection = ({ row }) => {
  const translate = useTranslate();
  const isTablet = useTablet();
  const unitValuePositive = row.allocatedQuantity > 0 ? row.allocatedQuantity : row.allocatedQuantity * -1;
  const transactionDate = new Date(row.transactionTimestamp);
  const formattedDate = format(transactionDate, "yyyy-MM-dd");
  return (
    <section className={styles.infoSection}>
      {isTablet ? (
        <>
          <div>
            <Label inline label={translate("PRODUCT_NUMBER_SHORT")} value={row.fixedAssetNumber} />
            <Label inline label={translate("ARTICLE_CLASS")} value={row.itemClass} />
          </div>
          <div className={styles.right}>
            {row?.allocatedQuantity > 0 && (
              <Label inline label={translate("HOLDING_RESERVED")} value={`${row.allocatedQuantity} ${row.unit}`} />
            )}
            {row.handedOverInformation && (
              <p className={(styles.text, styles.highlightedYellow)}> {row.handedOverInformation} </p>
            )}
            {row.handedOverDate && <Label inline label={translate("DELIVERED_DATE")} value={row.handedOverDate} />}
          </div>
          <div>
            <Label inline label={translate("INVENTORY_NUMBER_SHORT")} value={row.fixedAssetNumber} />
            <Label inline label={translate("BATCH")} value={row.batchNumber} />
          </div>
          {row.status && (
            <div>
              <div>{row.status}</div>
            </div>
          )}
        </>
      ) : (
        <>
          <div>
            <Label inline label={translate("PRODUCT_NUMBER_SHORT")} value={row.itemNumber} />
            <Label inline label={translate("ARTICLE_CLASS")} value={row.itemClass} />
          </div>
          <div>
            <Label inline label={translate("INVENTORY_NUMBER_SHORT")} value={row.fixedAssetNumber} />
            <Label inline label={translate("BATCH_NO")} value={row.batchNumber} />
          </div>
          <div>
            <div>{row.status}</div>
          </div>
          <div>
            {row?.allocatedQuantity > 0 && (
              <Label inline label={translate("HOLDING_RESERVED")} value={`${unitValuePositive} ${row.unit}`} />
            )}
            {row.handedOverInformation && (
              <p className={(styles.text, styles.highlightedYellow)}> {row.handedOverInformation} </p>
            )}
            {row.transactionTimestamp && <Label inline label={translate("RETURNED")} value={formattedDate} />}
          </div>
        </>
      )}
    </section>
  );
};
ClientHoldingList.propTypes = {
  onSelect: PropType.func,
  clientId: PropType.string,
  selected: PropType.arrayOf(
    PropType.shape({
      id: PropType.string,
      quantity: PropType.number,
    }),
  ),
  singleSelect: PropType.bool,
  multiSelect: PropType.bool,
  readOnly: PropType.bool,
  link: PropType.bool,
  amountChangeable: PropType.bool,
  onAmountChange: PropType.func,
  provider: PropType.func,
  referrer: PropType.string,
  maxSelectable: PropType.shape({
    amount: PropType.number,
    text: PropType.string,
  }),
  onlyInventories: PropType.bool,
  skipSearch: PropType.bool,
  disableAllocated: PropType.bool,
  isReceiverMenu: PropType.bool,
  isHistory: PropType.bool,
  holdingHistoryList: PropType.array,
};

Label.propTypes = {
  label: PropType.string,
  value: PropType.string,
};

ReadOnlyRow.propTypes = {
  row: PropType.object,
  link: PropType.bool,
  referrer: PropType.string,
};
HistoryReadOnlyRow.propTypes = {
  row: PropType.object,
  link: PropType.bool,
  referrer: PropType.string,
};

MultiSelectRow.propTypes = {
  selected: PropType.array,
  row: PropType.object,
  onSelect: PropType.func,
  amountChangeable: PropType.bool,
  onAmountChange: PropType.func,
  getQuantity: PropType.func,
  link: PropType.bool,
  toggleSelected: PropType.func,
  referrer: PropType.string,
  maxSelectableAmount: PropType.number,
  disableAllocated: PropType.bool,
  isReceiverMenu: PropType.bool,
};

SingleSelectRow.propTypes = {
  selected: PropType.array,
  row: PropType.object,
  onSelect: PropType.func,
  amountChangeable: PropType.bool,
  onAmountChange: PropType.func,
  getQuantity: PropType.func,
  link: PropType.bool,
  referrer: PropType.string,
};

InfoSection.propTypes = {
  row: PropType.object,
  link: PropType.bool,
};

ProductNumberLabel.propTypes = {
  row: PropType.object,
  link: PropType.bool,
};

HistoryInfoSection.propTypes = {
  row: PropType.object,
};

AccessorySection.propTypes = {
  row: PropType.object,
};

TitleName.propTypes = {
  row: PropType.object,
  link: PropType.bool,
  referrer: PropType.string,
  isReceiverMenu: PropType.bool,
};

export default ClientHoldingList;
